import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

const getButtonClasses = (className, disabled) => {
  const custom = className ? className.split(/\s+/) : [];

  const hasBg = custom.some(c => c.startsWith('bg-'));
  const hasBorder = custom.some(c => c.startsWith('border-') || c === 'border');
  const hasRounded = custom.some(c => c.startsWith('rounded-') || c === 'rounded');
  const hasShadow = custom.some(c => c.startsWith('shadow-') || c === 'shadow');
  const hasPaddingLeft = custom.some(c => c.startsWith('pl-') || c.startsWith('px-') || c.startsWith('p-'));
  const hasPaddingRight = custom.some(c => c.startsWith('pr-') || c.startsWith('px-') || c.startsWith('p-'));
  const hasPaddingY = custom.some(c => c.startsWith('py-') || c.startsWith('pt-') || c.startsWith('pb-') || c.startsWith('p-'));
  const hasWidth = custom.some(c => c.startsWith('w-') || c.startsWith('min-w-') || c.startsWith('max-w-') || c === 'flex-1');
  const hasTextSize = custom.some(c => c.match(/^text-(xs|sm|base|lg|xl|2xl)$/) || c.match(/^text-\[\d+px\]$/));
  const hasTextColor = custom.some(c => c.startsWith('text-') && !hasTextSize && c !== 'text-left' && c !== 'text-center' && c !== 'text-right' && c !== 'text-justify');
  const hasFontWeight = custom.some(c => c.startsWith('font-'));
  const hasTextTransform = custom.some(c => c === 'uppercase' || c === 'lowercase' || c === 'capitalize' || c === 'normal-case');
  const hasTracking = custom.some(c => c.startsWith('tracking-'));

  const baseClasses = [
    'flex', 'items-center', 'justify-between', 'gap-2', 'transition-all', 'text-left', 'hover:border-[var(--theme-primary)]'
  ];

  if (!hasBg) baseClasses.push('bg-white');
  if (!hasBorder) baseClasses.push('border', 'border-[var(--theme-tertiary)]');
  if (!hasRounded) baseClasses.push('rounded-md');
  if (!hasShadow) baseClasses.push('shadow-sm');
  if (!hasPaddingLeft) baseClasses.push('pl-3');
  if (!hasPaddingRight) baseClasses.push('pr-3');
  if (!hasPaddingY) baseClasses.push('py-2');
  if (!hasWidth) baseClasses.push('w-full');
  
  if (!hasTextSize) baseClasses.push('text-xs');
  if (!hasFontWeight) baseClasses.push('font-semibold');
  if (!hasTextColor) baseClasses.push('text-slate-700');
  if (!hasTextTransform) baseClasses.push('uppercase');
  if (!hasTracking && !custom.includes('normal-case')) baseClasses.push('tracking-[0.12em]');

  if (disabled) baseClasses.push('opacity-50', 'cursor-not-allowed');

  return [...baseClasses, ...custom].join(' ');
};

export const CustomSelect = ({
  value,
  onChange,
  options,
  disabled,
  className,
  placeholder,
  menuPlacement = 'bottom',
  showBadgeInButton = true,
  searchable = false,
  searchPlaceholder = 'Search options...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
    visibility: 'hidden',
  });

  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const updateDropdownPosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    
    let actualPlacement = menuPlacement;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow < 140 && spaceAbove > spaceBelow) {
      actualPlacement = 'top';
    }

    if (actualPlacement === 'top') {
      const maxH = Math.min(260, spaceAbove - 16);
      setDropdownStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: maxH,
        zIndex: 999999,
        visibility: 'visible',
      });
    } else {
      const maxH = Math.min(260, spaceBelow - 16);
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: maxH,
        zIndex: 999999,
        visibility: 'visible',
      });
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      updateDropdownPosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, menuPlacement]);

  // Focus search input smoothly without triggering browser layout scroll
  useEffect(() => {
    if (isOpen && searchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 40);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      const clickedInContainer = containerRef.current && containerRef.current.contains(event.target);
      const clickedInDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!clickedInContainer && !clickedInDropdown) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    const handleResize = () => {
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) => {
      if (opt.isHeader) return true;
      const labelMatch = String(opt.label || '').toLowerCase().includes(term);
      const badgeMatch = String(opt.badge || '').toLowerCase().includes(term);
      const subtextMatch = String(opt.subtext || '').toLowerCase().includes(term);
      const valMatch = String(opt.value || '').toLowerCase().includes(term);
      return labelMatch || badgeMatch || subtextMatch || valMatch;
    });
  }, [options, searchable, searchTerm]);

  const dropdownContent = isOpen && !disabled ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        ...dropdownStyle,
      }}
    >
      <div className="relative bg-white border border-[var(--theme-tertiary)] shadow-xl rounded-md flex flex-col py-1 overflow-y-auto custom-scrollbar" style={{ maxHeight: dropdownStyle.maxHeight || 256 }}>
        {searchable && (
          <div
            className="p-2 border-b border-[var(--theme-tertiary)] sticky top-0 bg-white z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none focus:bg-white focus:border-[var(--theme-primary)] transition-colors normal-case tracking-normal font-normal text-slate-800"
              />
            </div>
          </div>
        )}
        {filteredOptions.length === 0 ? (
          <div className="px-4 py-3 text-[11px] font-medium text-slate-400 italic text-center whitespace-nowrap">
            {searchTerm ? 'No matching results' : 'No options available'}
          </div>
        ) : (
          filteredOptions.map((opt, index) => {
            const isSelected = opt.value === value && opt.value !== '';
            return (
              <React.Fragment key={opt.value}>
                {opt.isHeader ? (
                  <div className={`px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.16em] bg-[var(--theme-secondary)] ${index > 0 ? 'mt-1' : ''}`}>
                    {opt.label}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { onChange(opt.value); setIsOpen(false); }}
                    className={`relative w-full flex items-center justify-between gap-3 px-4 py-2.5 text-slate-700 hover:bg-[var(--theme-secondary)] transition-colors border-l-[3px] text-left ${
                      isSelected 
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-secondary)] font-bold text-[var(--theme-primary)]' 
                        : 'border-transparent hover:border-[var(--theme-primary)]'
                    } ${
                      className?.includes('normal-case') 
                        ? 'text-xs font-medium' 
                        : 'text-xs font-semibold uppercase tracking-[0.12em]'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      {opt.icon && <span className="text-current shrink-0 flex items-center">{opt.icon}</span>}
                      <span className="truncate">{opt.label}</span>
                    </div>

                    {/* Unboxed plain barcode number */}
                    {opt.subtext && (
                      <span className={`font-mono text-xs shrink-0 tracking-wide ${isSelected ? 'text-[var(--theme-primary)] font-bold' : 'text-slate-400 font-semibold'}`}>
                        {opt.subtext}
                      </span>
                    )}

                    {/* Fallback traditional badge if supplied */}
                    {opt.badge && !opt.subtext && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-widest ${isSelected ? 'bg-[var(--theme-primary)] text-white' : 'bg-gray-100 text-slate-500'}`}>
                        {opt.badge}
                      </span>
                    )}
                  </button>
                )}
                {index < options.length - 1 && !opt.isHeader && !options[index + 1].isHeader && (
                  <div className="h-px bg-[var(--theme-tertiary)] mx-2 my-0.5 shrink-0"></div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${isOpen ? 'z-50' : 'z-20'} ${className?.includes('w-full') ? 'w-full' : ''}`} ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={getButtonClasses(className, disabled)}
      >
        <div className="truncate flex-1 min-w-0 flex items-center gap-2">
          {selectedOption?.icon && <span className="shrink-0 flex items-center">{selectedOption.icon}</span>}
          <span className="truncate">{(selectedOption && selectedOption.value !== '') ? selectedOption.label : (placeholder || 'Select...')}</span>
          {showBadgeInButton && selectedOption?.badge && selectedOption.value !== '' && (
            <span className="hidden sm:inline-block shrink-0 px-1.5 py-0.5 rounded border border-current opacity-60 text-[10px] font-extrabold uppercase tracking-widest">
              {selectedOption.badge}
            </span>
          )}
        </div>
        <ChevronDown size={14} className={`shrink-0 transition-transform text-slate-400 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};
